import _, { map, isEmpty } from 'underscore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Carousel } from '@bingads-webui-react/primitive-unthemed';
import { PlayPauseButtons } from '@bingads-webui-campaign-react/play-pause-buttons';
import { withUAPLThemedStyles } from '../with-themed-styles';
import { getAdPlacementComponent } from './get-ad-placement-component';
import { getCarouselLayoutStyles } from '../styles/layout-components/carousel-layout-styles';
import { pickSingleImageFromImageList } from '../ad-preview-model/use-ad-preview-model';
import { CustomCarousel } from './custom-carousel-component';

const autoRotateInterval = 3000;

export const CarouselLayout = ({
  activeIndex,
  setActiveIndex,
  classes,
  customCarouselRootClassName,
  customCarouselLayoutStyles,
  customPlayPauseButtonsStyles,
  enableAutoRotate,
  startAutoRotating,
  i18n,
  onRenderEmptyView,
  onRenderPlacement,
  onActiveIndexChange,
  onRenderHeader,
  placementList,
  disableDefaultStyles,
  useCustomCarousel,
  customCarouselWrapperClassName,
}) => {
  const [index, setIndex] = useState(0);

  // if external activeIndex is provided, use it, otherwise use the internal state
  const curActiveIndex = !_.isUndefined(activeIndex) ? activeIndex : index;
  const curSetIndex = setActiveIndex || setIndex;

  const [direction, setDirection] = useState('next');
  const [auto, setAuto] = useState(enableAutoRotate && startAutoRotating);
  const [startAutoRotateListner, setStartAutoRotateListner] = useState(false);

  const autoRotateListener = useRef(null);

  const emptyView = useMemo(() => {
    if (onRenderEmptyView) {
      return onRenderEmptyView(placementList);
    }
    return null;
  }, [onRenderEmptyView, placementList]);

  const placementComponentList = useMemo(() => {
    const ret = map(placementList, (placement, i) => {
      const { placementMeta, images, ...rest } = placement || {};
      const { placementType, hasMultiImages } = placementMeta || {};
      const AdPlacementComponent = getAdPlacementComponent(placementType);
      if (AdPlacementComponent) {
        // convert multi-image model to single-image model if needed
        let newPlacement = placement;
        if (!hasMultiImages && !isEmpty(images)) {
          const selectedImage = pickSingleImageFromImageList(images) || {};
          newPlacement = { placementMeta, ...selectedImage, ...rest };
        }
        return [onRenderPlacement(newPlacement, AdPlacementComponent, i), placement];
      }
      return [null, placement];
    });
    return ret;
  }, [onRenderPlacement, placementList]);

  const onRenderCarouselHeader = useCallback(() => {
    if (onRenderHeader) {
      const placement = placementList ? placementList[curActiveIndex] : null;
      const { placementMeta, images, ...rest } = placement || {};
      const { hasMultiImages } = placementMeta || {};
      let newPlacement = placement;
      if (!hasMultiImages && !isEmpty(images)) {
        const selectedImage = pickSingleImageFromImageList(images) || {};
        newPlacement = { placementMeta, ...selectedImage, ...rest };
      }
      return onRenderHeader(newPlacement, curActiveIndex);
    }
    return null;
  }, [curActiveIndex, onRenderHeader, placementList]);

  const clearAutoRotateListener = useCallback(() => {
    if (autoRotateListener.current) {
      clearTimeout(autoRotateListener.current);
      autoRotateListener.current = null;
    }
  }, []);

  const autoRotate = useCallback((currentIndex) => {
    if (!auto || isEmpty(placementList)) {
      // eslint-disable-next-line no-useless-return
      return;
    }

    setStartAutoRotateListner(true);

    const previewCount = placementList.length;
    let nextIndex = currentIndex + 1;
    if (nextIndex >= previewCount) {
      nextIndex = 0;
    }

    autoRotateListener.current = setTimeout(() => {
      curSetIndex(nextIndex);
      setDirection('next');
      autoRotate(nextIndex);
    }, (autoRotateInterval));
  }, [auto, curSetIndex, placementList]);

  const start = useCallback(() => {
    setDirection('next');
    setAuto(true);
  }, []);

  const stop = useCallback(() => {
    if (auto) {
      setAuto(false);
      clearAutoRotateListener();
      setStartAutoRotateListner(false);
    }
  }, [clearAutoRotateListener, auto]);

  const onSelect = useCallback((selectedIndex, e) => {
    stop();
    curSetIndex(selectedIndex || 0);
    setDirection(e.direction);
  }, [curSetIndex, stop]);

  // auto rotate at initialisation when auto rotate is enabled
  useEffect(() => {
    if (auto && !startAutoRotateListner) {
      autoRotate(curActiveIndex);
    }
  }, [autoRotate, auto, startAutoRotateListner, curActiveIndex]);

  useEffect(() => {
    if (onActiveIndexChange) {
      onActiveIndexChange(curActiveIndex);
    }
  }, [curActiveIndex, onActiveIndexChange]);

  useEffect(() => () => {
    clearAutoRotateListener();
  }, [clearAutoRotateListener]);

  useEffect(() => {
    if (placementList && placementList.length > 0 && curActiveIndex >= placementList.length) {
      setActiveIndex(0);
    }
  }, [curActiveIndex, placementList, setActiveIndex]);

  const CarouselComponent = useCustomCarousel ? CustomCarousel : Carousel;
  const moreCarouselProps = useMemo(
    () => (useCustomCarousel ? {
      carouselWrapperClassName: classnames(customCarouselWrapperClassName, classes.customCarouselWrapper),
    } : {}),
    [classes.customCarouselWrapper, customCarouselWrapperClassName, useCustomCarousel]
  );

  return !isEmpty(placementComponentList) ? (
    <div className={classnames(classes.carouselLayoutContainer, customCarouselRootClassName, 'new-preview-carousel-container-adprelib')}>
      <div className="preview-header">
        {onRenderCarouselHeader()}
      </div>
      <CarouselComponent
        activeIndex={curActiveIndex < placementList.length ? curActiveIndex : 0}
        direction={direction}
        controls={false}
        onSelect={onSelect}
        InjectedControls={enableAutoRotate ? <PlayPauseButtons
          newI18n={i18n}
          isPlaying={auto}
          play={start}
          pause={stop}
          customizedClassName={customPlayPauseButtonsStyles || classes.playPauseButtons}
        /> : null}
        nextLabel={i18n.getString(_TL_('Next ad preview'))}
        prevLabel={i18n.getString(_TL_('Previous ad preview'))}
        className={classnames(!disableDefaultStyles && classes.adPreviewCarousel, customCarouselLayoutStyles || classes.baseCarouselLayoutStyles, 'ad-placement-preview')}
        {...moreCarouselProps}
      >
        {
          placementComponentList.map(([placementComponent, placement], idx) => {
            const { placementMeta, effectiveImageUrl } = placement || {};
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Carousel.Item key={`key-${placementMeta ? placementMeta.placementType : idx}-${effectiveImageUrl}-${idx}`}>
                {placementComponent}
              </Carousel.Item>
            );
          })
        }
      </CarouselComponent>
    </div>
  ) : emptyView;
};

CarouselLayout.propTypes = {
  activeIndex: PropTypes.number,
  setActiveIndex: PropTypes.func,
  classes: PropTypes.shape({
    carouselLayoutContainer: PropTypes.string.isRequired,
    adPreviewCarousel: PropTypes.string.isRequired,
    baseCarouselLayoutStyles: PropTypes.string.isRequired,
    playPauseButtons: PropTypes.string.isRequired,
  }),
  customCarouselRootClassName: PropTypes.string,
  customCarouselLayoutStyles: PropTypes.string,
  customPlayPauseButtonsStyles: PropTypes.string,
  enableAutoRotate: PropTypes.bool,
  startAutoRotating: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onRenderEmptyView: PropTypes.func,
  onRenderHeader: PropTypes.func,
  onRenderPlacement: PropTypes.func.isRequired,
  placementList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onActiveIndexChange: PropTypes.func,
  disableDefaultStyles: PropTypes.bool,
  useCustomCarousel: PropTypes.bool,
  customCarouselWrapperClassName: PropTypes.string,
};

CarouselLayout.defaultProps = {
  activeIndex: undefined,
  setActiveIndex: undefined,
  customCarouselRootClassName: null,
  customCarouselLayoutStyles: null,
  customPlayPauseButtonsStyles: null,
  enableAutoRotate: false,
  startAutoRotating: true,
  onRenderEmptyView: null,
  onActiveIndexChange: null,
  onRenderHeader: null,
  disableDefaultStyles: false,
  useCustomCarousel: false,
  customCarouselWrapperClassName: null,
  classes: {},
};

export const AdPreviewCarouselLayout = withUAPLThemedStyles(CarouselLayout, getCarouselLayoutStyles, { componentName: 'AdPreviewCarouselLayout' });

export const UnstyledAdPreviewCarouselLayout = CarouselLayout;

// when trying to reuse the Carousel instance with different children count, the carousel behaves
// abnormal, seems its internal states are messed up, fix it by creating a new component when the
// resetOn prop changes
export const AdPreviewCarouselLayoutEx = (props) => {
  const { resetOn } = props;
  const LayoutComponent = useCallback(
    props2 => <AdPreviewCarouselLayout {...props2} resetOn={resetOn} />,
    [resetOn]
  );
  return <LayoutComponent {...props} />;
};

// todo: in addition to above issue, another issue is that the left and right buttons and
// the indicator dots are not in the same parent element, which make it hard to align them,
// need to refactor the Carousel component to fix it
